import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Check, Circle, Trash2, FolderOpen, Layers, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/store/AppContext";
import { Card, PageHeader, Overline, EmptyState, Button } from "@/components/ui-kit";

const Projects: React.FC = () => {
  const {
    data,
    addProject,
    removeProject,
    moveProject,
    addTask,
    toggleTask,
    removeTask,
  } = useApp();
  const navigate = useNavigate();

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState<string>("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [taskDraft, setTaskDraft] = useState<Record<string, string>>({});

  const role = (id: string | null) => data.roles.find((r) => r.id === id) ?? null;

  const tasksByProject = useMemo(() => {
    const map: Record<string, typeof data.tasks> = {};
    for (const t of data.tasks) {
      const key = t.projectId ?? "__none__";
      (map[key] ||= []).push(t);
    }
    return map;
  }, [data.tasks]);

  const create = () => {
    if (!name.trim()) return;
    addProject(name, roleId || null);
    setName("");
    setRoleId("");
    setAdding(false);
    toast.success("Projet créé.");
  };

  const addTaskTo = (pid: string) => {
    const val = (taskDraft[pid] || "").trim();
    if (!val) return;
    addTask(val, pid);
    setTaskDraft((d) => ({ ...d, [pid]: "" }));
  };

  return (
    <div>
      <PageHeader
        overline="Ton travail"
        title="Sur quoi avances-tu ?"
        right={
          <button
            onClick={() => navigate("/roles")}
            data-testid="open-roles"
            aria-label="Gérer les rôles"
            className="h-11 w-11 rounded-full bg-white border border-forest-900/10 flex items-center justify-center text-forest-700"
          >
            <Layers strokeWidth={1.75} className="h-5 w-5" />
          </button>
        }
      />

      {!adding ? (
        <Button full variant="secondary" onClick={() => setAdding(true)} data-testid="add-project-button" className="mb-8">
          <Plus className="h-5 w-5" /> Nouveau projet
        </Button>
      ) : (
        <Card className="p-5 mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-forest-700">Nouveau projet</span>
            <button onClick={() => setAdding(false)} data-testid="cancel-project" aria-label="Annuler">
              <X className="h-4 w-4 text-forest-500" />
            </button>
          </div>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
            data-testid="project-name-input"
            placeholder="Nom du projet"
            className="w-full bg-cream-surface border border-forest-900/5 rounded-xl px-4 py-3 text-forest-900 placeholder:text-forest-500/50 focus:outline-none focus:ring-2 focus:ring-sage/40 mb-3"
          />
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            data-testid="project-role-select"
            className="w-full bg-cream-surface border border-forest-900/5 rounded-xl px-4 py-3 text-forest-800 focus:outline-none focus:ring-2 focus:ring-sage/40 mb-4"
          >
            <option value="">Sans rôle</option>
            {data.roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={create}
            disabled={!name.trim()}
            data-testid="save-project"
            className="w-full rounded-full bg-sage text-white py-3 font-medium disabled:opacity-40"
          >
            Créer le projet
          </motion.button>
        </Card>
      )}

      {data.projects.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FolderOpen strokeWidth={1.5} className="h-7 w-7" />}
            title="Aucun projet pour l'instant"
            hint="Crée ton premier projet et rattache-le à un rôle."
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {data.projects.map((p, i) => {
            const tasks = tasksByProject[p.id] ?? [];
            const done = tasks.filter((t) => t.done).length;
            const r = role(p.roleId);
            const open = openId === p.id;
            return (
              <Card key={p.id} className="overflow-hidden" data-testid={`project-${p.id}`}>
                <div className="flex items-stretch">
                  <div className="flex flex-col justify-center gap-1 pl-3 shrink-0">
                    <button
                      onClick={() => moveProject(p.id, "up")}
                      disabled={i === 0}
                      data-testid={`move-project-up-${p.id}`}
                      aria-label="Monter le projet"
                      className="h-6 w-6 flex items-center justify-center rounded-lg text-forest-500 hover:bg-forest-900/5 disabled:opacity-25 disabled:hover:bg-transparent"
                    >
                      <ChevronUp className="h-4 w-4" strokeWidth={2} />
                    </button>
                    <button
                      onClick={() => moveProject(p.id, "down")}
                      disabled={i === data.projects.length - 1}
                      data-testid={`move-project-down-${p.id}`}
                      aria-label="Descendre le projet"
                      className="h-6 w-6 flex items-center justify-center rounded-lg text-forest-500 hover:bg-forest-900/5 disabled:opacity-25 disabled:hover:bg-transparent"
                    >
                      <ChevronDown className="h-4 w-4" strokeWidth={2} />
                    </button>
                  </div>
                  <button
                    onClick={() => setOpenId(open ? null : p.id)}
                    data-testid={`project-header-${p.id}`}
                    className="flex-1 min-w-0 flex items-center gap-3 py-5 pr-5 pl-3 text-left"
                  >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      {r && (
                        <span
                          className="text-[11px] font-medium px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: `${r.color}22`, color: "#4A5449" }}
                        >
                          {r.name}
                        </span>
                      )}
                    </div>
                    <p className="font-heading text-xl font-normal tracking-tight text-forest-900 truncate">
                      {p.name}
                    </p>
                    <p className="text-xs text-forest-500 mt-1">
                      {tasks.length === 0
                        ? "Aucune tâche"
                        : `${done}/${tasks.length} tâche${tasks.length > 1 ? "s" : ""} terminée${
                            done > 1 ? "s" : ""
                          }`}
                    </p>
                  </div>
                  <ChevronRight
                    className={`h-5 w-5 text-forest-500 transition-transform ${open ? "rotate-90" : ""}`}
                  />
                </button>
                </div>

                <AnimatePresence>
                  {open && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 border-t border-forest-900/5 pt-4">
                        <div className="space-y-1.5 mb-3">
                          {tasks.map((t) => (
                            <div key={t.id} className="flex items-center gap-3 group" data-testid={`task-${t.id}`}>
                              <button
                                onClick={() => toggleTask(t.id)}
                                data-testid={`toggle-task-${t.id}`}
                                aria-label="Basculer la tâche"
                              >
                                {t.done ? (
                                  <div className="h-5 w-5 rounded-full bg-sage flex items-center justify-center">
                                    <Check className="h-3 w-3 text-white" />
                                  </div>
                                ) : (
                                  <Circle strokeWidth={1.5} className="h-5 w-5 text-forest-500/40" />
                                )}
                              </button>
                              <span
                                className={`flex-1 text-sm ${
                                  t.done ? "text-forest-500 line-through" : "text-forest-800"
                                }`}
                              >
                                {t.text}
                              </span>
                              <button
                                onClick={() => removeTask(t.id)}
                                data-testid={`delete-task-${t.id}`}
                                aria-label="Supprimer la tâche"
                                className="p-1 text-forest-500/50 hover:text-blush opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="flex gap-2">
                          <input
                            value={taskDraft[p.id] || ""}
                            onChange={(e) =>
                              setTaskDraft((d) => ({ ...d, [p.id]: e.target.value }))
                            }
                            onKeyDown={(e) => e.key === "Enter" && addTaskTo(p.id)}
                            data-testid={`task-input-${p.id}`}
                            placeholder="Ajouter une tâche…"
                            className="flex-1 bg-cream-surface border border-forest-900/5 rounded-xl px-4 py-2.5 text-sm text-forest-900 placeholder:text-forest-500/50 focus:outline-none focus:ring-2 focus:ring-sage/40"
                          />
                          <button
                            onClick={() => addTaskTo(p.id)}
                            data-testid={`add-task-${p.id}`}
                            aria-label="Ajouter"
                            className="h-10 w-10 rounded-xl bg-sage/12 text-sage-dark flex items-center justify-center shrink-0"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>

                        <button
                          onClick={() => {
                            removeProject(p.id);
                            toast("Projet supprimé.");
                          }}
                          data-testid={`delete-project-${p.id}`}
                          className="mt-4 text-xs text-forest-500/70 hover:text-blush inline-flex items-center gap-1.5"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Supprimer le projet
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Projects;
